import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useInventory } from '../../contexts/InventoryContext';
import { useAlert } from '../../contexts/AlertContext';

const Container = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  background: rgba(255, 255, 255, 0.1);
  padding: 20px;
  border-radius: 15px;
  backdrop-filter: blur(10px);
`;

const Title = styled.h1`
  color: white;
  margin: 0;
  font-size: 2.5rem;
  font-weight: 700;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const Button = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
  }
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 20px;
  margin-bottom: 30px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatsCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  padding: 25px;
  border-radius: 15px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const StatsTitle = styled.h3`
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 10px 0;
  font-size: 1rem;
  font-weight: 500;
`;

const StatsValue = styled.div`
  color: white;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 5px;
`;

const StatsSubtext = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.1);
  padding: 25px;
  border-radius: 15px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const CardTitle = styled.h3`
  color: white;
  margin: 0 0 20px 0;
  font-size: 1.5rem;
  font-weight: 600;
`;

const WarehouseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const WarehouseItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 20px;
  border-radius: 10px;
  border-left: 4px solid ${props => props.status === 'active' ? '#4ade80' : props.status === 'maintenance' ? '#fbbf24' : '#ef4444'};
`;

const WarehouseHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const WarehouseName = styled.h4`
  color: white;
  margin: 0;
  font-size: 1.2rem;
`;

const StatusBadge = styled.span`
  background: ${props => props.status === 'active' ? '#4ade80' : props.status === 'maintenance' ? '#fbbf24' : '#ef4444'};
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
`;

const WarehouseMetrics = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
  margin-top: 15px;
`;

const MetricItem = styled.div`
  text-align: center;
`;

const MetricLabel = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8rem;
  margin-bottom: 5px;
`;

const MetricValue = styled.div`
  color: white;
  font-weight: 600;
`;

const ActivityFeed = styled.div`
  max-height: 400px;
  overflow-y: auto;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(79, 172, 254, 0.5);
    }
  }
`;

const ActivityItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 10px;
  border-left: 3px solid #3b82f6;
`;

const ActivityTime = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.8rem;
  margin-bottom: 5px;
`;

const ActivityDescription = styled.div`
  color: white;
  font-size: 0.9rem;
`;

const TabContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TabList = styled.div`
  display: flex;
  gap: 5px;
  background: rgba(255, 255, 255, 0.1);
  padding: 5px;
  border-radius: 10px;
`;

const Tab = styled.button`
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  background: ${props => props.active ? 'rgba(255, 255, 255, 0.2)' : 'transparent'};
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const TabContent = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 20px;
  border-radius: 10px;
  min-height: 300px;
`;

const ShipmentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const ShipmentItem = styled.div`
  background: rgba(255, 255, 255, 0.1);
  padding: 15px;
  border-radius: 8px;
  border-left: 3px solid ${props => props.status === 'delivered' ? '#4ade80' : props.status === 'in-transit' ? '#fbbf24' : '#3b82f6'};
`;

const ShipmentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const ShipmentId = styled.h5`
  color: white;
  margin: 0;
`;

const WarehouseOperations = () => {
  const { warehouses, inventoryItems } = useInventory();
  const { addAlert } = useAlert();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [warehouseStats, setWarehouseStats] = useState({
    totalWarehouses: 0,
    activeWarehouses: 0,
    totalCapacity: 0,
    utilizationRate: 0,
    pendingShipments: 0,
    completedShipments: 0
  });
  
  const [recentActivity, setRecentActivity] = useState([]);
  const [shipments, setShipments] = useState([]);

  useEffect(() => {
    // Generate mock data
    const mockWarehouses = [
      {
        id: 'WH001',
        name: 'Central Warehouse',
        location: 'New York, NY',
        status: 'active',
        capacity: 10000,
        utilized: 7500,
        temperature: 22,
        humidity: 45,
        commodities: ['Gold', 'Silver', 'Platinum']
      },
      {
        id: 'WH002',
        name: 'East Coast Storage',
        location: 'Miami, FL',
        status: 'active',
        capacity: 8000,
        utilized: 5200,
        temperature: 24,
        humidity: 50,
        commodities: ['Coffee', 'Sugar', 'Cocoa']
      },
      {
        id: 'WH003',
        name: 'West Coast Facility',
        location: 'Los Angeles, CA',
        status: 'maintenance',
        capacity: 12000,
        utilized: 9000,
        temperature: 20,
        humidity: 40,
        commodities: ['Copper', 'Aluminum']
      }
    ];

    const mockShipments = [
      {
        id: 'SH001',
        commodity: 'Gold',
        quantity: '500 oz',
        from: 'WH001',
        to: 'Customer A',
        status: 'in-transit',
        estimatedDelivery: '2024-01-15'
      },
      {
        id: 'SH002',
        commodity: 'Silver',
        quantity: '1000 oz',
        from: 'WH002',
        to: 'Customer B',
        status: 'delivered',
        estimatedDelivery: '2024-01-12'
      },
      {
        id: 'SH003',
        commodity: 'Coffee',
        quantity: '50 bags',
        from: 'WH002',
        to: 'Customer C',
        status: 'pending',
        estimatedDelivery: '2024-01-18'
      }
    ];

    const mockActivity = [
      {
        time: '2 minutes ago',
        description: '500 oz Gold received at Central Warehouse',
        type: 'receipt'
      },
      {
        time: '15 minutes ago',
        description: 'Temperature alert resolved at East Coast Storage',
        type: 'alert'
      },
      {
        time: '1 hour ago',
        description: '1000 oz Silver shipped from Central Warehouse',
        type: 'shipment'
      },
      {
        time: '2 hours ago',
        description: 'Quality inspection completed for Coffee batch CF-001',
        type: 'inspection'
      },
      {
        time: '3 hours ago',
        description: 'West Coast Facility maintenance scheduled',
        type: 'maintenance'
      }
    ];

    setWarehouseStats({
      totalWarehouses: mockWarehouses.length,
      activeWarehouses: mockWarehouses.filter(w => w.status === 'active').length,
      totalCapacity: mockWarehouses.reduce((sum, w) => sum + w.capacity, 0),
      utilizationRate: Math.round((mockWarehouses.reduce((sum, w) => sum + w.utilized, 0) / mockWarehouses.reduce((sum, w) => sum + w.capacity, 0)) * 100),
      pendingShipments: mockShipments.filter(s => s.status === 'pending').length,
      completedShipments: mockShipments.filter(s => s.status === 'delivered').length
    });

    setRecentActivity(mockActivity);
    setShipments(mockShipments);
  }, []);

  const handleAddWarehouse = () => {
    addAlert({
      type: 'system',
      severity: 'low',
      title: 'Add Warehouse',
      message: 'New warehouse registration form opened',
      category: 'system'
    });
  };

  const handleScheduleMaintenance = () => {
    addAlert({
      type: 'system',
      severity: 'medium',
      title: 'Maintenance Scheduled',
      message: 'Warehouse maintenance has been scheduled',
      category: 'system'
    });
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  const formatCapacity = (utilized, capacity) => {
    return `${formatNumber(utilized)} / ${formatNumber(capacity)} m³`;
  };

  const getUtilizationColor = (rate) => {
    if (rate >= 90) return '#ef4444';
    if (rate >= 75) return '#fbbf24';
    return '#4ade80';
  };

  return (
    <Container>
      <Header>
        <Title>🏭 Warehouse Operations</Title>
        <ActionButtons>
          <Button onClick={handleAddWarehouse}>
            ➕ Add Warehouse
          </Button>
          <Button onClick={handleScheduleMaintenance}>
            🔧 Schedule Maintenance
          </Button>
        </ActionButtons>
      </Header>

      <DashboardGrid>
        <StatsCard>
          <StatsTitle>Total Warehouses</StatsTitle>
          <StatsValue>{warehouseStats.totalWarehouses}</StatsValue>
          <StatsSubtext>{warehouseStats.activeWarehouses} active</StatsSubtext>
        </StatsCard>
        
        <StatsCard>
          <StatsTitle>Total Capacity</StatsTitle>
          <StatsValue>{formatNumber(warehouseStats.totalCapacity)} m³</StatsValue>
          <StatsSubtext>Storage space</StatsSubtext>
        </StatsCard>
        
        <StatsCard>
          <StatsTitle>Utilization Rate</StatsTitle>
          <StatsValue style={{ color: getUtilizationColor(warehouseStats.utilizationRate) }}>
            {warehouseStats.utilizationRate}%
          </StatsValue>
          <StatsSubtext>Current usage</StatsSubtext>
        </StatsCard>
        
        <StatsCard>
          <StatsTitle>Active Shipments</StatsTitle>
          <StatsValue>{warehouseStats.pendingShipments}</StatsValue>
          <StatsSubtext>{warehouseStats.completedShipments} completed</StatsSubtext>
        </StatsCard>
      </DashboardGrid>

      <ContentGrid>
        <Card>
          <CardTitle>🏢 Warehouse Facilities</CardTitle>
          <WarehouseList>
            {warehouses?.map((warehouse) => (
              <WarehouseItem key={warehouse.id} status={warehouse.status}>
                <WarehouseHeader>
                  <WarehouseName>{warehouse.name}</WarehouseName>
                  <StatusBadge status={warehouse.status}>{warehouse.status}</StatusBadge>
                </WarehouseHeader>
                <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem', marginBottom: '10px' }}>
                  📍 {warehouse.location}
                </div>
                <WarehouseMetrics>
                  <MetricItem>
                    <MetricLabel>Capacity</MetricLabel>
                    <MetricValue>{formatCapacity(warehouse.utilized, warehouse.capacity)}</MetricValue>
                  </MetricItem>
                  <MetricItem>
                    <MetricLabel>Temperature</MetricLabel>
                    <MetricValue>{warehouse.temperature}°C</MetricValue>
                  </MetricItem>
                  <MetricItem>
                    <MetricLabel>Humidity</MetricLabel>
                    <MetricValue>{warehouse.humidity}%</MetricValue>
                  </MetricItem>
                </WarehouseMetrics>
              </WarehouseItem>
            ))}
          </WarehouseList>
        </Card>

        <Card>
          <TabContainer>
            <TabList>
              <Tab 
                active={activeTab === 'overview'}
                onClick={() => setActiveTab('overview')}
              >
                📊 Overview
              </Tab>
              <Tab 
                active={activeTab === 'shipments'}
                onClick={() => setActiveTab('shipments')}
              >
                🚚 Shipments
              </Tab>
              <Tab 
                active={activeTab === 'activity'}
                onClick={() => setActiveTab('activity')}
              >
                📋 Activity
              </Tab>
            </TabList>

            <TabContent>
              {activeTab === 'overview' && (
                <div>
                  <h4 style={{ color: 'white', marginBottom: '20px' }}>Operations Overview</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div>
                      <MetricLabel>Average Utilization</MetricLabel>
                      <MetricValue style={{ fontSize: '1.5rem', color: getUtilizationColor(warehouseStats.utilizationRate) }}>
                        {warehouseStats.utilizationRate}%
                      </MetricValue>
                    </div>
                    <div>
                      <MetricLabel>Active Locations</MetricLabel>
                      <MetricValue style={{ fontSize: '1.5rem' }}>{warehouseStats.activeWarehouses}</MetricValue>
                    </div>
                    <div>
                      <MetricLabel>Daily Throughput</MetricLabel>
                      <MetricValue style={{ fontSize: '1.5rem' }}>245 tons</MetricValue>
                    </div>
                    <div>
                      <MetricLabel>Efficiency Score</MetricLabel>
                      <MetricValue style={{ fontSize: '1.5rem', color: '#4ade80' }}>94%</MetricValue>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'shipments' && (
                <div>
                  <h4 style={{ color: 'white', marginBottom: '20px' }}>Recent Shipments</h4>
                  <ShipmentsList>
                    {shipments.map((shipment) => (
                      <ShipmentItem key={shipment.id} status={shipment.status}>
                        <ShipmentHeader>
                          <ShipmentId>{shipment.id}</ShipmentId>
                          <StatusBadge status={shipment.status}>{shipment.status}</StatusBadge>
                        </ShipmentHeader>
                        <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem' }}>
                          {shipment.quantity} {shipment.commodity} • {shipment.from} → {shipment.to}
                        </div>
                        <div style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.8rem', marginTop: '5px' }}>
                          ETA: {shipment.estimatedDelivery}
                        </div>
                      </ShipmentItem>
                    ))}
                  </ShipmentsList>
                </div>
              )}

              {activeTab === 'activity' && (
                <div>
                  <h4 style={{ color: 'white', marginBottom: '20px' }}>Recent Activity</h4>
                  <ActivityFeed>
                    {recentActivity.map((activity, index) => (
                      <ActivityItem key={index}>
                        <ActivityTime>{activity.time}</ActivityTime>
                        <ActivityDescription>{activity.description}</ActivityDescription>
                      </ActivityItem>
                    ))}
                  </ActivityFeed>
                </div>
              )}
            </TabContent>
          </TabContainer>
        </Card>
      </ContentGrid>
    </Container>
  );
};

export default WarehouseOperations; 